/**
 * Profile drawer для /messenger — открывается по клику на аватарку/имя в шапке.
 * DM:    профиль партнёра (Phone/Username/Bio + табы Медиа/Файлы/Голосовые/Ссылки)
 * Group: инфо группы + список участников (+ табы)
 *
 * Архитектура: fixed overlay (z:950) поверх всего, slide-in справа, ESC закрывает.
 */
import { useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchTopicMedia, fetchTopicMembers, fetchUserPublic, type TopicMediaPhoto,
  chatAttachmentPreviewUrl, chatAttachmentUrl, chatVideoThumbnailUrl, userAvatarUrl, topicAvatarUrl,
  type ChatTopic, type ChatTopicMember, type PublicUser,
} from "@/api/dashboard";
import { Avatar } from "@/components/prototype/Avatar";
import { isOnline, parseBackendIso } from "@/lib/presence";
import { useAuth } from "@/auth/AuthContext";
import { Icon } from "@/components/prototype/Icon";
import { PhotoLightbox, type LightboxImage } from "@/components/PhotoLightbox";
import { VideoLightbox } from "@/components/VideoLightbox";
import { VoiceMessage } from "@/components/VoiceMessage";
import { EditGroupModal } from "@/components/EditGroupModal";
import { EditBotModal } from "@/components/EditBotModal";
import { BOT_SALARY_USER_ID } from "@/api/dashboard";
import { orgsLine } from "@/api/orgs";

type DmTab = "media" | "files" | "voice" | "links";
type GroupTab = "members" | "media" | "files" | "voice" | "links";

function formatLastSeen(iso: string | null | undefined): { text: string; online: boolean } {
  if (!iso) return { text: "был(а) давно", online: false };
  // Backend отдаёт naive UTC ("YYYY-MM-DD HH:MM:SS" без 'Z') — без явной 'Z'
  // браузер парсит как LOCAL time, и в Москве (UTC+3) diff всегда +3 часа.
  const isoZ = iso.endsWith("Z") ? iso : (iso.includes("T") ? iso + "Z" : iso.replace(" ", "T") + "Z");
  const t = new Date(isoZ).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - t) / 60000);
  if (diffMin < 3) return { text: "в сети", online: true };
  if (diffMin < 60) return { text: `был(а) ${diffMin} мин назад`, online: false };
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return { text: `был(а) ${diffH} ч назад`, online: false };
  if (diffH < 48) return { text: "был(а) вчера", online: false };
  return { text: (parseBackendIso(iso) ?? new Date(NaN)).toLocaleDateString("ru-RU"), online: false };
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Администратор",
  owner: "Владелец",
  head_op: "Руководитель ОП",
  op_manager: "Менеджер ОП",
  assistant: "Ассистент",
  field_engineer: "Выездной инженер",
  builder: "Строитель",
  foreman: "Бригадир",
  engineer: "Инженер",
  kc_manager: "Менеджер КЦ",
};

export function ProfileDrawer({
  topic, isOpen, onClose, myUserId, onJumpToMessage, onReplyToMessage, onOpenUserProfile, onDeleteMessage, onForwardMessage, onMessageUser,
}: {
  topic: ChatTopic | null;
  isOpen: boolean;
  onClose: () => void;
  myUserId: string | null;
  onJumpToMessage?: (messageId: number, topicId?: number) => void;
  onReplyToMessage?: (messageId: number) => void;
  onOpenUserProfile?: (userId: string) => void;
  onDeleteMessage?: (messageId: number) => void;
  onForwardMessage?: (messageId: number) => void;
  onMessageUser?: (userId: string) => void;
}) {
  const isDm = topic?.kind === "dm" && !!topic?.dm_partner;
  const partnerId = isDm ? topic!.dm_partner!.user_id : null;

  const [dmTab, setDmTab] = useState<DmTab>("media");
  const [groupTab, setGroupTab] = useState<GroupTab>("members");
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [videoLb, setVideoLb] = useState<{ src: string; filename: string } | null>(null);
  type CtxItemKind = "photo" | "video" | "file" | "voice" | "link";
  const { user: authUser } = useAuth();
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editBotOpen, setEditBotOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{
    x: number; y: number;
    messageId: number; topicId: number;
    attachmentId: number;   // 0 для link (нет аттача)
    filename: string;
    kind: CtxItemKind;
    mime: string;
    canDelete: boolean;
    linkUrl?: string;        // для link
  } | null>(null);
  const ctxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ctxMenu) return;
    const onClick = () => setCtxMenu(null);
    window.addEventListener("click", onClick);
    window.addEventListener("scroll", onClick, true);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onClick, true);
    };
  }, [ctxMenu]);

  function openCtxMenu(
    e: ReactMouseEvent,
    messageId: number,
    topicId: number,
    attachmentId: number,
    filename: string,
    kind: CtxItemKind,
    mime: string,
    authorUserId: string | null,
    linkUrl?: string
  ) {
    e.preventDefault();
    e.stopPropagation();
    const canDelete = !!myUserId && (authorUserId === myUserId);
    setCtxMenu({ x: e.clientX, y: e.clientY, messageId, topicId, attachmentId, filename, kind, mime, canDelete, linkUrl });
  }

  // Позиционирование: 5px от клика. Если менюшка ближе 30px к краю — флипаем
  // на противоположную сторону клика, и в этой стороне держим 50px от края.
  useEffect(() => {
    if (!ctxMenu) return;
    const el = ctxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 5;
    const NEAR_EDGE = 30;
    const FLIP_MARGIN = 50;
    // Default: clickX+GAP, clickY+GAP
    let nx = ctxMenu.x + GAP;
    let ny = ctxMenu.y + GAP;
    // X flip
    if (nx + rect.width > vw - NEAR_EDGE) {
      nx = ctxMenu.x - rect.width - GAP;
      if (nx < FLIP_MARGIN) nx = FLIP_MARGIN;
    }
    // Y flip
    if (ny + rect.height > vh - NEAR_EDGE) {
      ny = ctxMenu.y - rect.height - GAP;
      if (ny < FLIP_MARGIN) ny = FLIP_MARGIN;
    }
    el.style.left = `${nx}px`;
    el.style.top = `${ny}px`;
  }, [ctxMenu]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const publicQ = useQuery<PublicUser>({
    queryKey: ["profile-public", partnerId],
    queryFn: () => fetchUserPublic(partnerId!),
    enabled: !!partnerId && isOpen,
    staleTime: 60_000,
  });

  const membersQ = useQuery<ChatTopicMember[]>({
    queryKey: ["topic-members-drawer", topic?.id],
    queryFn: () => fetchTopicMembers(topic!.id),
    enabled: !!topic?.id && (topic?.id ?? 0) > 0 && isOpen && !isDm,
    staleTime: 60_000,
  });

  const mediaQ = useQuery({
    queryKey: ["topic-media-drawer", topic?.id],
    queryFn: () => fetchTopicMedia(topic!.id),
    enabled: !!topic?.id && topic.id > 0 && isOpen,
    staleTime: 30_000,
  });

  // Доп. страницы фото («Показать ещё») — копятся локально для текущего топика.
  // В личке показываем медиа ЭТОГО диалога (07.10.2026): раньше тянули всё, что
  // человек отправлял где угодно, и фото разных чатов смешивались.
  const [morePhotos, setMorePhotos] = useState<{ topicId: number; items: TopicMediaPhoto[]; hasMore: boolean } | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);

  if (!isOpen || !topic) return null;

  const partner = topic.dm_partner;
  const lastSeen = isDm ? formatLastSeen(publicQ.data?.last_seen_at) : null;
  const members = membersQ.data ?? [];
  const isSuper = !!authUser?.is_superuser;
  const isCreator = !!topic?.created_by_user_id && topic?.created_by_user_id === myUserId;
  const myMember = members.find(m => m.user_id === myUserId);
  const canManageGroup = isSuper || isCreator || (myMember?.role === "admin");
  const photos = mediaQ.data?.photos ?? [];
  const files = mediaQ.data?.files ?? [];
  const voices = mediaQ.data?.voices ?? [];
  const links = mediaQ.data?.links ?? [];
  const extraPhotos = morePhotos && morePhotos.topicId === topic.id ? morePhotos : null;
  const allPhotos: TopicMediaPhoto[] = extraPhotos ? [...photos, ...extraPhotos.items] : photos;
  const hasMorePhotos = extraPhotos ? extraPhotos.hasMore : !!mediaQ.data?.has_more_photos;
  const isVideoMime = (m: string | null | undefined) => (m || "").startsWith("video/");
  async function loadMorePhotos() {
    const last = allPhotos[allPhotos.length - 1];
    if (!last || moreLoading) return;
    setMoreLoading(true);
    try {
      const r = await fetchTopicMedia(topic!.id, last.attachment_id);
      setMorePhotos(prev => ({
        topicId: topic!.id,
        items: [...(prev && prev.topicId === topic!.id ? prev.items : []), ...r.photos],
        hasMore: !!r.has_more_photos,
      }));
    } catch {
      /* кнопка остаётся, можно повторить */
    } finally {
      setMoreLoading(false);
    }
  }
  const photoGrid = (
    <>
      <div className="profile-media-grid">
        {allPhotos.map((ph, i) => {
          const isVideo = isVideoMime(ph.mime);
          return (
            <div
              key={ph.attachment_id}
              className="profile-media-item"
              onClick={() => {
                if (isVideo) setVideoLb({ src: chatAttachmentUrl(ph.attachment_id), filename: ph.filename });
                else setLightboxIdx(allPhotos.slice(0, i).filter(x => !isVideoMime(x.mime)).length);
              }}
              onContextMenu={(e) => openCtxMenu(e, ph.message_id, topic!.id, ph.attachment_id, ph.filename, isVideo ? "video" : "photo", ph.mime || "", ph.author_user_id ?? null)}
              style={{ cursor: "pointer" }}
            >
              <img src={isVideo ? chatVideoThumbnailUrl(ph.attachment_id) : chatAttachmentPreviewUrl(ph.attachment_id)} alt={ph.filename} loading="lazy"/>
              {isVideo && <div className="profile-media-play"><Icon name="play" size={18}/></div>}
            </div>
          );
        })}
      </div>
      {hasMorePhotos && (
        <button type="button" className="profile-more-btn" onClick={() => void loadMorePhotos()} disabled={moreLoading}>
          {moreLoading ? "Загружаю…" : "Показать ещё"}
        </button>
      )}
    </>
  );
  const mediaTab = mediaQ.isLoading ? <div className="profile-empty">Загрузка…</div>
    : mediaQ.error ? <div className="profile-empty">Ошибка: {String((mediaQ.error as Error).message || mediaQ.error)}</div>
    : allPhotos.length === 0 ? <div className="profile-empty">Нет фотографий</div>
    : photoGrid;
  const filesTab = files.length === 0 ? <div className="profile-empty">Нет файлов</div> : (
    <>{files.map(f => (
      <div
        key={f.attachment_id}
        className="profile-file-item"
        style={{ cursor: "pointer" }}
        onClick={() => { window.open(chatAttachmentUrl(f.attachment_id), "_blank"); }}
        onContextMenu={(e) => openCtxMenu(e, f.message_id, topic!.id, f.attachment_id, f.filename, "file", f.mime || "", f.author_user_id ?? null)}
      >
        <div className="profile-file-icon"><Icon name="file" size={20}/></div>
        <div className="profile-file-info">
          <div className="profile-file-name">{f.filename}</div>
          <div className="profile-file-meta">{fmtSize(f.size_bytes)}</div>
        </div>
      </div>
    ))}</>
  );
  const voiceTab = voices.length === 0 ? <div className="profile-empty">Нет голосовых сообщений</div> : (
    <>{voices.map(v => (
      <div key={v.attachment_id} className="profile-voice-row" onContextMenu={(e) => openCtxMenu(e, v.message_id, topic!.id, v.attachment_id, "voice.ogg", "voice", v.mime || "", v.author_user_id ?? null)}>
        <VoiceMessage
          src={chatAttachmentUrl(v.attachment_id)}
          attachmentId={v.attachment_id}
          durationSec={v.duration_sec}
          waveform={v.waveform}
          sizeBytes={v.size_bytes}
        />
      </div>
    ))}</>
  );
  const linksTab = links.length === 0 ? <div className="profile-empty">Нет ссылок</div> : (
    <>{links.map((l, i) => (
      <div
        key={i}
        className="profile-link-item"
        style={{ cursor: "pointer" }}
        onClick={() => window.open(l.url, "_blank", "noopener,noreferrer")}
        onContextMenu={(e) => openCtxMenu(e, l.message_id, topic!.id, 0, l.url, "link", "", l.author_user_id ?? null, l.url)}
      >
        <div className="profile-link-icon"><Icon name="link" size={20}/></div>
        <div className="profile-link-info">
          <div className="profile-link-title">{l.url}</div>
          <div className="profile-link-url">{l.author_name}</div>
        </div>
      </div>
    ))}</>
  );
  const lightbox = lightboxIdx !== null && allPhotos.some(p => !isVideoMime(p.mime)) ? (() => {
    const imgs: LightboxImage[] = allPhotos
      .filter(p => !isVideoMime(p.mime))
      .map(p => ({ src: chatAttachmentUrl(p.attachment_id), filename: p.filename }));
    const idx = Math.min(lightboxIdx, imgs.length - 1);
    return <PhotoLightbox images={imgs} idx={idx} setIdx={setLightboxIdx} onClose={() => setLightboxIdx(null)}/>;
  })() : null;

  // ─── DM view ──────────────────────────────────────────────────
  if (isDm && partner) {
    const userAvatar = userAvatarUrl(partner.user_id, partner.avatar_filename);
    return (
      <>
      <aside className="profile-drawer">
          <header className="profile-header">
            <button className="profile-back" onClick={onClose} title="Закрыть">
              <Icon name="chevron_l" size={20}/>
            </button>
            <span className="profile-header-title">Профиль</span>
          </header>
          <div className="profile-main">
            <div className="profile-top">
              <div className="profile-avatar-wrap">
                {userAvatar ? (
                  <img className="profile-avatar-img" src={userAvatar} alt={partner.display_name || "?"}/>
                ) : (
                  <Avatar name={partner.display_name || "?"} userId={partner.user_id} avatarFilename={null} size={100}/>
                )}
              </div>
              <div className="profile-name">{partner.display_name || "Без имени"}</div>
              <div className={`profile-status${lastSeen?.online ? " online" : ""}`}>{lastSeen?.text || "—"}</div>
            </div>

            <div className="profile-info">
              {publicQ.data?.phone && (
                <div className="profile-info-item">
                  <div className="profile-info-label">Телефон</div>
                  <div className="profile-info-value">
                    <a href={`tel:${publicQ.data.phone}`} style={{ color: "var(--tg-link, #3390ec)", textDecoration: "none" }}>
                      {publicQ.data.phone}
                    </a>
                  </div>
                </div>
              )}
              {publicQ.data?.username && (
                <div className="profile-info-item">
                  <div className="profile-info-label">Имя пользователя</div>
                  <div className="profile-info-value">@{publicQ.data.username}</div>
                </div>
              )}
              {publicQ.data?.bio && (
                <div className="profile-info-item">
                  <div className="profile-info-label">О себе</div>
                  <div className="profile-info-value">{publicQ.data.bio}</div>
                </div>
              )}
              {(publicQ.data?.orgs?.length ?? 0) > 0 && (
                <div className="profile-info-item">
                  <div className="profile-info-label">Организации</div>
                  <div className="profile-info-value">{orgsLine(publicQ.data?.orgs)}</div>
                </div>
              )}
              {publicQ.data?.role && ROLE_LABELS[publicQ.data.role] && (
                <div className="profile-info-item">
                  <div className="profile-info-label">Роль</div>
                  <div className="profile-info-value">{ROLE_LABELS[publicQ.data.role]}</div>
                </div>
              )}
              {publicQ.data?.birthday && (
                <div className="profile-info-item">
                  <div className="profile-info-label">День рождения</div>
                  <div className="profile-info-value">{publicQ.data.birthday}</div>
                </div>
              )}
            </div>

            {/* === Two action buttons: Написать + Позвонить — скрываем для бота
                  (DM с ним уже открыт, звонить ему некому). === */}
            {partner.user_id !== BOT_SALARY_USER_ID && (
            <div className="profile-dm-actions">
              <button
                type="button"
                className="profile-dm-action-btn"
                onClick={async () => {
                  if (!onMessageUser) return;
                  onMessageUser(partner.user_id);
                  onClose();
                }}
              >
                <Icon name="msg" size={20}/>
                <span>Написать</span>
              </button>
              <button
                type="button"
                className="profile-dm-action-btn"
                disabled={!publicQ.data?.phone}
                onClick={() => {
                  if (publicQ.data?.phone) {
                    window.location.href = `tel:${publicQ.data.phone}`;
                  }
                }}
              >
                <Icon name="phone" size={20}/>
                <span>Позвонить</span>
              </button>
            </div>
            )}

            {/* Admin-only: редактирование оформления бота зарплаты */}
            {isSuper && partner.user_id === BOT_SALARY_USER_ID && (
              <button
                type="button"
                className="profile-edit-btn"
                onClick={() => setEditBotOpen(true)}
              >
                <Icon name="edit" size={18}/>
                <span>Редактировать бота</span>
              </button>
            )}

            <div className="profile-tabs">
              {(["media", "files", "voice", "links"] as const).map(t => (
                <button
                  key={t}
                  className={`profile-tab${dmTab === t ? " active" : ""}`}
                  onClick={() => setDmTab(t)}
                >{t === "media" ? "Медиа" : t === "files" ? "Файлы" : t === "voice" ? "Голосовые" : "Ссылки"}</button>
              ))}
            </div>

            <div className="profile-tab-content active">
              {dmTab === "media" && mediaTab}
              {dmTab === "files" && filesTab}
              {dmTab === "voice" && voiceTab}
              {dmTab === "links" && linksTab}
            </div>
          </div>
      </aside>
      {editBotOpen && partner.user_id === BOT_SALARY_USER_ID && (
        <EditBotModal
          currentDisplayName={partner.display_name || "Бот зарплаты"}
          currentAvatarFilename={partner.avatar_filename ?? null}
          onClose={() => setEditBotOpen(false)}
        />
      )}
      {lightbox}
      {videoLb && (
        <VideoLightbox
          src={videoLb.src}
          filename={videoLb.filename}
          onClose={() => setVideoLb(null)}
        />
      )}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className="profile-ctx-menu"
          style={{ position: "fixed", top: ctxMenu.y, left: ctxMenu.x, zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {onJumpToMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onJumpToMessage(ctxMenu.messageId, ctxMenu.topicId); setCtxMenu(null); }}>
              <Icon name="check" size={16}/> Перейти к сообщению
            </button>
          )}
          {ctxMenu.kind !== "link" && (
            <button type="button" className="profile-ctx-item" onClick={() => {
              const a = document.createElement("a");
              a.href = chatAttachmentUrl(ctxMenu.attachmentId);
              a.download = ctxMenu.filename || "file";
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              setCtxMenu(null);
            }}>
              <Icon name="save" size={16}/> Сохранить как…
            </button>
          )}
          {ctxMenu.kind === "link" && ctxMenu.linkUrl && (
            <button type="button" className="profile-ctx-item" onClick={() => {
              window.open(ctxMenu.linkUrl!, "_blank", "noopener,noreferrer");
              setCtxMenu(null);
            }}>
              <Icon name="external" size={16}/> Открыть в новой вкладке
            </button>
          )}
          {(ctxMenu.kind === "photo" || (ctxMenu.mime || "").toLowerCase().startsWith("image/")) && (
            <button type="button" className="profile-ctx-item" onClick={async () => {
              try {
                const resp = await fetch(chatAttachmentUrl(ctxMenu.attachmentId), { credentials: "include" });
                const blob = await resp.blob();
                if (navigator.clipboard && (window as any).ClipboardItem) {
                  await (navigator.clipboard as any).write([new (window as any).ClipboardItem({ [blob.type]: blob })]);
                }
              } catch (e) { console.error(e); alert("Не удалось скопировать изображение"); }
              setCtxMenu(null);
            }}>
              <Icon name="copy" size={16}/> Скопировать изображение
            </button>
          )}
          <button type="button" className="profile-ctx-item" onClick={() => {
            const url = ctxMenu.kind === "link" && ctxMenu.linkUrl
              ? ctxMenu.linkUrl
              : window.location.origin + chatAttachmentUrl(ctxMenu.attachmentId);
            navigator.clipboard?.writeText(url);
            setCtxMenu(null);
          }}>
            <Icon name="external" size={16}/> Скопировать ссылку
          </button>
          {onReplyToMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onReplyToMessage(ctxMenu.messageId); setCtxMenu(null); }}>
              <Icon name="reply" size={16}/> Ответить
            </button>
          )}
          {onForwardMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onForwardMessage(ctxMenu.messageId); setCtxMenu(null); }}>
              <Icon name="external" size={16}/> Переслать
            </button>
          )}
          {onDeleteMessage && ctxMenu.canDelete && (
            <button type="button" className="profile-ctx-item profile-ctx-danger" onClick={() => {
              if (window.confirm("Удалить сообщение?")) onDeleteMessage(ctxMenu.messageId);
              setCtxMenu(null);
            }}>
              <Icon name="trash" size={16}/> Удалить
            </button>
          )}
        </div>
      )}
      </>
    );
  }

  // ─── Group view ───────────────────────────────────────────────
  const onlineCount = members.filter(member => isOnline(member.last_seen_at)).length;

  return (
    <>
    <aside className="profile-drawer">
        <header className="profile-header">
          <button className="profile-back" onClick={onClose} title="Закрыть">
            <Icon name="chevron_l" size={20}/>
          </button>
          <span className="profile-header-title">Информация о группе</span>
        </header>
        <div className="profile-main">
          <div className="profile-top">
            <div className="profile-avatar-wrap" style={{ background: "var(--tg-accent-light, #e8f5e9)" }}>
              {(() => {
                // Приоритет: загруженная картинка → emoji → инициалы
                if (topic.avatar_filename) {
                  const url = topicAvatarUrl(topic.id, topic.avatar_filename);
                  if (url) return <img src={url} alt="" style={{ width: 100, height: 100, borderRadius: "50%", objectFit: "cover" }}/>;
                }
                if (topic.icon) {
                  const ic = topic.icon;
                  if (ic.startsWith("pack:")) {
                    const n = parseInt(ic.slice(5), 10);
                    if (Number.isFinite(n) && n >= 1 && n <= 254) {
                      return <img src={`/icons/icon-${n}.png`} alt=""
                        style={{ width: 100, height: 100, objectFit: "contain", borderRadius: "50%" }}/>;
                    }
                  }
                  if (!ic.startsWith("pack:") && !ic.startsWith("svg:") && !ic.startsWith("lottie:") && !ic.startsWith("lottie_url:") && !ic.startsWith("http")) {
                    return <span style={{ fontSize: 56, lineHeight: 1 }}>{ic}</span>;
                  }
                }
                return <Avatar name={topic.name} userId={null} avatarFilename={null} size={100}/>;
              })()}
            </div>
            <div className="profile-name">{topic.name}</div>
            <div className="profile-status">
              {members.length} {members.length === 1 ? "участник" : members.length < 5 ? "участника" : "участников"}
              {onlineCount > 0 ? `, ${onlineCount} онлайн` : ""}
            </div>
          </div>

          <div className="profile-info">
            <div className="profile-info-item">
              <div className="profile-info-label">Тип</div>
              <div className="profile-info-value">
                {topic.kind === "private_group" ? "Приватная группа" : "Публичный канал"}
              </div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-label">ID топика</div>
              <div className="profile-info-value">#{topic.id}</div>
            </div>
          </div>
          {canManageGroup && (
          <button
            type="button"
            className="profile-edit-btn"
            onClick={() => setEditGroupOpen(true)}
          >
            <Icon name="edit" size={18}/>
            <span>Редактировать группу</span>
          </button>
          )}

          <div className="profile-tabs">
            {(["members", "media", "files", "voice", "links"] as const).map(t => (
              <button
                key={t}
                className={`profile-tab${groupTab === t ? " active" : ""}`}
                onClick={() => setGroupTab(t)}
              >{t === "members" ? "Участники" : t === "media" ? "Медиа" : t === "files" ? "Файлы" : t === "voice" ? "Голосовые" : "Ссылки"}</button>
            ))}
          </div>

          <div className="profile-tab-content active">
            {groupTab === "members" && (
              members.length === 0 ? <div className="profile-empty">Нет участников</div> : (
                <>{members.map(m => (
                  <div
                    key={m.user_id}
                    className="profile-member"
                    style={{ cursor: onOpenUserProfile ? "pointer" : "default" }}
                    onClick={() => { if (onOpenUserProfile) onOpenUserProfile(m.user_id); }}
                  >
                    <div className="profile-member-avatar">
                      <Avatar
                        name={m.display_name || "?"}
                        userId={m.user_id}
                        avatarFilename={m.avatar_filename}
                        size={42}
                        online={isOnline(m.last_seen_at)}
                      />
                    </div>
                    <div className="profile-member-info">
                      <div className="profile-member-name">
                        {m.display_name || "Без имени"}
                        {m.user_id === myUserId && <span className="profile-member-you"> (вы)</span>}
                      </div>
                      <div className="profile-member-role">
                        {(m.role === "admin" ? "Модератор" : "Участник")
                          + (() => {
                            const isMe = m.user_id === myUserId;
                            const label = (isMe && m.user_role === "admin")
                              ? "Владелец"
                              : (m.user_role && ROLE_LABELS[m.user_role]) || "";
                            return label ? ` • ${label}` : "";
                          })()}
                      </div>
                    </div>
                  </div>
                ))}</>
              )
            )}
            {groupTab === "media" && mediaTab}
            {groupTab === "files" && filesTab}
            {groupTab === "voice" && voiceTab}
            {groupTab === "links" && linksTab}
          </div>
        </div>
    </aside>
    {lightbox}
    {videoLb && (
      <VideoLightbox
        src={videoLb.src}
        filename={videoLb.filename}
        onClose={() => setVideoLb(null)}
      />
    )}
    {editGroupOpen && !isDm && (
      <EditGroupModal topic={topic} myUserId={myUserId} onClose={() => setEditGroupOpen(false)}/>
    )}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className="profile-ctx-menu"
          style={{ position: "fixed", top: ctxMenu.y, left: ctxMenu.x, zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {onJumpToMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onJumpToMessage(ctxMenu.messageId); setCtxMenu(null); }}>
              <Icon name="check" size={16}/> Перейти к сообщению
            </button>
          )}
          {ctxMenu.kind !== "link" && (
            <button type="button" className="profile-ctx-item" onClick={() => {
              const a = document.createElement("a");
              a.href = chatAttachmentUrl(ctxMenu.attachmentId);
              a.download = ctxMenu.filename || "file";
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              setCtxMenu(null);
            }}>
              <Icon name="save" size={16}/> Сохранить как…
            </button>
          )}
          {ctxMenu.kind === "link" && ctxMenu.linkUrl && (
            <button type="button" className="profile-ctx-item" onClick={() => {
              window.open(ctxMenu.linkUrl!, "_blank", "noopener,noreferrer");
              setCtxMenu(null);
            }}>
              <Icon name="external" size={16}/> Открыть в новой вкладке
            </button>
          )}
          {(ctxMenu.kind === "photo" || (ctxMenu.mime || "").toLowerCase().startsWith("image/")) && (
            <button type="button" className="profile-ctx-item" onClick={async () => {
              try {
                const resp = await fetch(chatAttachmentUrl(ctxMenu.attachmentId), { credentials: "include" });
                const blob = await resp.blob();
                if (navigator.clipboard && (window as any).ClipboardItem) {
                  await (navigator.clipboard as any).write([new (window as any).ClipboardItem({ [blob.type]: blob })]);
                }
              } catch (e) { console.error(e); alert("Не удалось скопировать изображение"); }
              setCtxMenu(null);
            }}>
              <Icon name="copy" size={16}/> Скопировать изображение
            </button>
          )}
          <button type="button" className="profile-ctx-item" onClick={() => {
            const url = ctxMenu.kind === "link" && ctxMenu.linkUrl
              ? ctxMenu.linkUrl
              : window.location.origin + chatAttachmentUrl(ctxMenu.attachmentId);
            navigator.clipboard?.writeText(url);
            setCtxMenu(null);
          }}>
            <Icon name="external" size={16}/> Скопировать ссылку
          </button>
          {onReplyToMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onReplyToMessage(ctxMenu.messageId); setCtxMenu(null); }}>
              <Icon name="reply" size={16}/> Ответить
            </button>
          )}
          {onForwardMessage && (
            <button type="button" className="profile-ctx-item" onClick={() => { onForwardMessage(ctxMenu.messageId); setCtxMenu(null); }}>
              <Icon name="external" size={16}/> Переслать
            </button>
          )}
          {onDeleteMessage && ctxMenu.canDelete && (
            <button type="button" className="profile-ctx-item profile-ctx-danger" onClick={() => {
              if (window.confirm("Удалить сообщение?")) onDeleteMessage(ctxMenu.messageId);
              setCtxMenu(null);
            }}>
              <Icon name="trash" size={16}/> Удалить
            </button>
          )}
        </div>
      )}
  </>
  );
}
